import { useEffect } from 'react';

/** Matches any Engrain Sightmap embed (property-agnostic). */
const SIGHTMAP_IFRAME_SELECTOR = 'iframe[src*="sightmap.com/embed"]';
const SIGHTMAP_WRAP_CLASS = 'sightmap-click-wrap';
const SIGHTMAP_OVERLAY_CLASS = 'sightmap-click-overlay';
const SIGHTMAP_STYLE_ID = 'sightmap-click-to-activate-styles';

const SIGHTMAP_STYLES = `
.sightmap-click-wrap {
  position: relative;
  width: 100%;
}
.sightmap-click-wrap iframe {
  display: block;
  width: 100%;
  border: none;
}
.sightmap-click-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  margin: 0;
  padding: 0;
  border: none;
  cursor: pointer;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sightmap-click-overlay[hidden] {
  display: none !important;
}
.sightmap-click-overlay__label {
  pointer-events: none;
  font-family: var(--vibe-font-body, system-ui, sans-serif);
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  padding: 0.7rem 1.1rem;
  border-radius: 2px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}
.sightmap-click-overlay:focus-visible {
  outline: 2px solid var(--vibe-color-primary, #2563eb);
  outline-offset: -2px;
}
`;

function ensureStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SIGHTMAP_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = SIGHTMAP_STYLE_ID;
  style.textContent = SIGHTMAP_STYLES;
  document.head.appendChild(style);
}

/**
 * Click-to-activate for Engrain Sightmap iframes (any property embed URL).
 *
 * - Default: overlay captures pointer events so the page scrolls over the map
 * - Click overlay: activate map interaction
 * - Click outside the map: deactivate again
 *
 * Portable: copy this file into another property theme and call
 * `useSightmapClickToActivate(true)` from a component that mounts on the
 * Interactive Map page (e.g. PageBlurb when tokenPrefix is interactive-map-blurb).
 */
export function useSightmapClickToActivate(enabled: boolean) {
  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;

    ensureStyles();

    let wrap: HTMLElement | null = null;
    let overlay: HTMLButtonElement | null = null;
    let activated = false;

    const setActivated = (next: boolean) => {
      activated = next;
      if (!overlay || !wrap) return;
      wrap.dataset.activated = next ? 'true' : 'false';
      overlay.hidden = next;
      overlay.setAttribute('aria-hidden', next ? 'true' : 'false');
      overlay.tabIndex = next ? -1 : 0;
    };

    const onOverlayActivate = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      setActivated(true);
    };

    const onDocumentPointerDown = (event: Event) => {
      if (!activated || !wrap) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (wrap.contains(target)) return;
      setActivated(false);
    };

    const ensureWrap = () => {
      const iframe = document.querySelector(SIGHTMAP_IFRAME_SELECTOR) as HTMLIFrameElement | null;
      if (!iframe) return false;

      iframe.style.setProperty('display', 'block');
      iframe.style.setProperty('border', 'none');
      iframe.setAttribute('width', '100%');

      wrap = iframe.closest(`.${SIGHTMAP_WRAP_CLASS}`) as HTMLElement | null;
      if (!wrap) {
        wrap = document.createElement('div');
        wrap.className = SIGHTMAP_WRAP_CLASS;
        wrap.dataset.activated = 'false';
        iframe.parentNode?.insertBefore(wrap, iframe);
        wrap.appendChild(iframe);

        overlay = document.createElement('button');
        overlay.type = 'button';
        overlay.className = SIGHTMAP_OVERLAY_CLASS;
        overlay.setAttribute('aria-label', 'Click to interact with the interactive map');
        overlay.innerHTML =
          '<span class="sightmap-click-overlay__label">Click to explore map</span>';
        overlay.addEventListener('click', onOverlayActivate);
        wrap.appendChild(overlay);
      } else {
        overlay = wrap.querySelector(`.${SIGHTMAP_OVERLAY_CLASS}`) as HTMLButtonElement | null;
      }

      setActivated(activated);
      return true;
    };

    ensureWrap();
    const observer = new MutationObserver(() => {
      ensureWrap();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('pointerdown', onDocumentPointerDown, true);

    return () => {
      observer.disconnect();
      document.removeEventListener('pointerdown', onDocumentPointerDown, true);
      document.querySelectorAll(`.${SIGHTMAP_WRAP_CLASS}`).forEach((node) => {
        const iframe = node.querySelector('iframe');
        if (iframe && node.parentNode) {
          node.parentNode.insertBefore(iframe, node);
          node.remove();
        }
      });
    };
  }, [enabled]);
}

export default useSightmapClickToActivate;
